import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import LoginModal from "@/components/LoginModal";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Heart, HandHeart, Stethoscope, ShieldCheck, MapPin, PhoneOff, Activity,
  Siren, Smartphone, ShoppingBag, CheckCircle2, ArrowRight, Users, Building2, Loader2, Flame,
} from "lucide-react";

const easeOut = [0.16, 1, 0.3, 1];
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: easeOut, delay }}>{children}</motion.div>
);

const IMG = {
  // Elegant Indian couple centred in frame — both faces clearly visible (Pexels · free)
  family: "https://images.pexels.com/photos/23489331/pexels-photo-23489331.jpeg?auto=compress&cs=tinysrgb&w=1100",
  // Sophisticated elderly Indian mother in a fine silk sari with glasses
  aunty: "https://images.pexels.com/photos/14961876/pexels-photo-14961876.jpeg?auto=compress&cs=tinysrgb&w=800",
};

// Decorative paisley SVG (repeated as ornament / divider)
const Paisley = ({ className = "" }) => (
  <svg viewBox="0 0 120 40" className={className} fill="none" aria-hidden="true">
    <path d="M6 20 C 6 10, 22 6, 30 14 C 38 22, 26 30, 18 24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="60" cy="20" r="4" fill="currentColor" />
    <circle cx="50" cy="20" r="1.5" fill="currentColor" />
    <circle cx="70" cy="20" r="1.5" fill="currentColor" />
    <path d="M114 20 C 114 10, 98 6, 90 14 C 82 22, 94 30, 102 24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const PACKAGES = [
  { id: "companion", name: "Companion", price: "4,999", tagline: "Companionship & daily support",
    features: ["8 Sewa Pal visits / month", "Companionship & conversation", "Tech help & family video calls", "Errands & groceries", "Family dashboard access"], highlight: false },
  { id: "care_plus", name: "Care+", price: "11,999", tagline: "Companionship + clinical care",
    features: ["12 Sewa Pal visits + 4 nurse visits", "Monthly home diagnostics", "Doctor teleconsults", "Medication management", "Masked-call caregiver contact", "Real-time family dashboard"], highlight: true },
  { id: "total_care", name: "Total Care", price: "24,999", tagline: "24/7 comprehensive eldercare",
    features: ["Daily Sewa Pal + dedicated nurse", "Fortnightly visiting doctor", "24/7 SOS response", "Full diagnostics + physiotherapy", "Continuous tracking", "Priority masked support"], highlight: false },
];

export default function Eldercare() {
  const [loginOpen, setLoginOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enq, setEnq] = useState({ company: "", contact_name: "", email: "", phone: "", employees: "", message: "" });
  const [sending, setSending] = useState(false);

  const goPlans = () => user && user.role === "family" ? navigate("/dashboard") : setLoginOpen(true);

  const submitEnquiry = async () => {
    if (!enq.contact_name || (!enq.email && !enq.phone)) { toast.error("Name and email or phone are required"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/eldercare/enquiry", enq);
      const c = data?.channels || {};
      const bits = [];
      if (c.user_email) bits.push("email");
      if (c.user_sms) bits.push("SMS");
      if (bits.length) {
        toast.success(`Enquiry sent — confirmation on your ${bits.join(" & ")}. Our team will reach out within 1 business day.`);
      } else {
        toast.success("Enquiry received. Our team will reach out within 1 business day.", {
          description: "We couldn't deliver an automated confirmation, but your enquiry is safely recorded.",
        });
      }
      setEnq({ company: "", contact_name: "", email: "", phone: "", employees: "", message: "" });
    } catch {
      toast.error("Could not send enquiry");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#FAFAFA] overflow-x-hidden">
      <Navbar onOpenLogin={() => setLoginOpen(true)} />
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} onSuccess={() => navigate("/dashboard")} />

      {/* HERO */}
      <section className="relative pt-32 pb-24 lg:pt-40 px-6 lg:px-10 overflow-hidden">
        {/* Warm marigold radial glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-3xl"
               style={{ background: "radial-gradient(circle, rgba(254,243,199,0.75) 0%, rgba(254,243,199,0.35) 45%, rgba(255,255,255,0) 75%)" }} />
        </div>

        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-100 text-[#92400E] text-xs font-bold uppercase tracking-[0.15em] mb-8">
              <Flame className="w-3.5 h-3.5" /> Hybrid Eldercare · India-first
            </div>

            {/* MAGNIFIED MAA PAPA CARE BRAND MARK */}
            <div className="relative mb-6">
              <span aria-hidden className="absolute -top-4 -left-2 text-[9rem] md:text-[13rem] font-serif-logo font-bold text-amber-100/60 select-none leading-none">माँ</span>
              <h1 data-testid="veda-brand-mark" className="relative font-serif-logo text-[#7A1E38] font-bold tracking-tight leading-[0.92] text-[3.5rem] sm:text-[5rem] md:text-[6rem] lg:text-[7rem]">
                Maa <span className="text-[#7A1E38]">Papa</span> <span className="text-[#D97706]">Care</span>
              </h1>
              <div className="mt-3 flex items-center gap-3">
                <span className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent max-w-[80px]" />
                <span className="text-[#B45309] font-serif-logo italic text-lg md:text-xl">सेवा · सम्मान · सुरक्षा</span>
                <span className="h-px flex-1 bg-gradient-to-l from-transparent via-amber-400/60 to-transparent max-w-[80px]" />
              </div>
              <p className="mt-1 text-xs uppercase tracking-[0.4em] font-semibold text-[#9A7B4F]">Seva · Samman · Suraksha</p>
            </div>

            <h2 className="font-head font-light text-[#041E26] text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight mt-8">
              Maa-Baap ki fikar, ab <span className="text-[#D97706] font-normal">humari zimmedari.</span>
            </h2>
            <p className="mt-6 text-lg text-slate-700 max-w-xl leading-relaxed">
              From a warm cup of chai in the morning to a nurse holding their hand at 2 AM — Maa Papa Care brings the whole <span className="italic text-[#7A1E38] font-medium">parivaar</span> home to your parents, wherever you are. Trusted Sewa Pals, home nurses, doctors and a 24/7 SOS — built for Indian families and NRIs who care fiercely.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button data-testid="veda-explore-plans" onClick={goPlans} className="rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-bold h-14 px-8 text-base group shadow-md">
                Explore Care Plans <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button data-testid="veda-b2b-cta" onClick={() => document.getElementById("veda-b2b")?.scrollIntoView({ behavior: "smooth" })} variant="outline" className="rounded-full border-[#7A1E38]/30 text-[#7A1E38] font-semibold h-14 px-8 text-base hover:bg-[#7A1E38] hover:text-white hover:border-[#7A1E38]">
                For Employers (B2B)
              </Button>
            </div>

            <div className="mt-10 flex items-center gap-6 text-xs text-slate-500">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> Police-verified staff</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> Hindi, English, Tamil, Bangla</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> ABDM-linked</div>
            </div>
          </div>

          {/* Right column — sophisticated Indian family imagery */}
          <Reveal className="relative">
            <div className="relative">
              {/* Decorative frame */}
              <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-amber-200/60 via-transparent to-[#7A1E38]/10 blur-2xl" />
              <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(122,30,56,0.25)] border-4 border-white">
                <img src={IMG.family} alt="Elegant Indian elderly parents in traditional attire" className="w-full h-[520px] object-cover object-[center_25%]" />
                {/* subtle bottom gradient for legibility of caption chip below */}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#041E26]/50 to-transparent pointer-events-none" />
                <div className="absolute bottom-4 left-4">
                  <div className="bg-white/95 backdrop-blur px-3.5 py-2 rounded-full shadow-md flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-[11px] font-semibold text-[#041E26]">Live · Kapoor uncle just video-called his daughter in Boston</span>
                  </div>
                </div>
              </div>

              {/* Floating Sanskrit blessing chip — sits OUTSIDE the image, top-right corner */}
              <div className="absolute -top-5 -right-5 bg-[#7A1E38] text-amber-100 rounded-2xl px-4 py-3 shadow-xl">
                <p className="font-serif-logo italic text-sm leading-tight">मातृ देवो भव</p>
                <p className="font-serif-logo italic text-sm leading-tight">पितृ देवो भव</p>
                <p className="text-[10px] uppercase tracking-widest opacity-70 mt-1">Our parents, our gods</p>
              </div>

              {/* Floating vitals card — sits OUTSIDE the image, bottom-left corner */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-3.5 shadow-xl flex items-center gap-3 border border-amber-100">
                <Activity className="w-4 h-4 text-[#B45309]" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">Live now</div>
                  <div className="text-sm font-semibold text-[#041E26]">Nurse Meera · vitals recorded ✓</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Paisley divider */}
        <div className="max-w-7xl mx-auto mt-20 flex items-center justify-center text-[#D97706]/40">
          <Paisley className="w-32 h-10" />
          <div className="mx-6 w-1.5 h-1.5 rounded-full bg-[#D97706]/50" />
          <Paisley className="w-32 h-10 scale-x-[-1]" />
        </div>
      </section>

      {/* TWO-TIER WORKFORCE */}
      <section id="workforce" className="py-20 lg:py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-14">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#D97706]">दो हाथ, एक परिवार · Two hands, one family</span>
            <h2 className="font-head text-4xl md:text-5xl font-light text-[#041E26] mt-4 tracking-tight">
              A <span className="italic text-[#7A1E38]">Sewa&nbsp;Pal</span> for the heart. A nurse for the health.
            </h2>
            <p className="mt-4 text-slate-600 text-lg">Because eldercare in India is never just medical — it's chai, prayers, memories, and the reassurance that someone <em>respectful</em> is with them.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="rounded-3xl border border-slate-200 bg-white p-8 h-full">
                <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-5"><HandHeart className="w-7 h-7 text-[#D97706]" /></div>
                <h3 className="font-head text-2xl font-semibold text-[#041E26]">Sewa Pals <span className="text-sm text-slate-400 font-normal ml-1">· सेवा&nbsp;पाल</span></h3>
                <p className="text-slate-500 mt-2">Warm, background-verified companions for everyday life — like a nephew or niece who visits often.</p>
                <ul className="mt-5 space-y-3">
                  {[[Heart, "Companionship, chai & conversation"], [Smartphone, "Video calls with children abroad, WhatsApp help"], [ShoppingBag, "Sabzi mandi, bills, temple visits, festival prep"], [Users, "Walks in the park, doctor visits, small errands"]].map(([Ic, t]) => (
                    <li key={t} className="flex items-center gap-3 text-slate-700"><span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center"><Ic className="w-4 h-4 text-[#041E26]" /></span>{t}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-[#0A2F3C] bg-[#041E26] p-8 h-full text-white">
                <div className="w-14 h-14 rounded-2xl bg-[#F59E0B]/20 flex items-center justify-center mb-5"><Stethoscope className="w-7 h-7 text-[#F59E0B]" /></div>
                <h3 className="font-head text-2xl font-semibold">Certified clinical care <span className="text-sm text-white/50 font-normal ml-1">· वैद्य&nbsp;सेवा</span></h3>
                <p className="text-white/60 mt-2">Registered nurses, MBBS doctors, physiotherapists — hospital-grade care at their bedside.</p>
                <ul className="mt-5 space-y-3">
                  {[[Stethoscope, "Home nurses & visiting doctors (Hindi/English)"], [Activity, "Home diagnostics, BP, sugar, ECG"], [Siren, "24/7 SOS with ambulance tie-ups"], [Heart, "Physiotherapy & chronic care (Diabetes, BP, Arthritis)"]].map(([Ic, t]) => (
                    <li key={t} className="flex items-center gap-3 text-white/90"><span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center"><Ic className="w-4 h-4 text-[#F59E0B]" /></span>{t}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VOICES FROM THE PARIVAAR — testimonial with Indian imagery */}
      <section className="px-6 lg:px-10 pb-8">
        <div className="max-w-7xl mx-auto rounded-[2rem] bg-gradient-to-br from-[#FFF7EB] via-[#FDF2E9] to-[#FEF3C7] p-8 lg:p-14 grid lg:grid-cols-5 gap-10 items-center border border-amber-100">
          <div className="lg:col-span-2 relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-[#7A1E38]/10 blur-xl" />
            <img src={IMG.aunty} alt="Sophisticated elderly Indian mother in a silk sari with glasses" className="relative rounded-[1.75rem] w-full h-[380px] object-cover object-center shadow-xl border-4 border-white" />
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-4 py-2 rounded-full shadow-md">
              <p className="text-xs font-semibold text-[#7A1E38]">— Sudha Malhotra ji · 71 · Gurgaon</p>
            </div>
          </div>
          <div className="lg:col-span-3">
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309]">परिवार की आवाज़ · Voices from the parivaar</span>
            <blockquote className="font-head text-2xl md:text-3xl font-light text-[#041E26] leading-relaxed tracking-tight mt-4">
              <span className="text-4xl text-[#D97706] leading-none">"</span>
              Aditi is in San Francisco, and I in Gurgaon. Every morning my Sewa&nbsp;Pal Neha helps me with my yoga, records my BP for her, and then we do pooja together.
              <span className="italic text-[#7A1E38]"> Ab akelapan nahi lagta.</span>
            </blockquote>
            <div className="mt-6 flex items-center gap-5 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Flame className="w-4 h-4 text-[#D97706]" /> 4,200+ elders cared for</div>
              <div className="hidden sm:flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-green-700" /> 4.9 · Parents' rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST & SAFETY */}
      <section className="bg-[#F3F4F6] py-20 lg:py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#D97706]">भरोसा · Trust &amp; safety architecture</span>
            <h2 className="font-head text-4xl md:text-5xl font-light text-[#041E26] mt-4 tracking-tight">Man&nbsp;ki shanti, engineered in.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[[ShieldCheck, "Background-checked staff", "Every caregiver is police-verified and reference-checked."], [MapPin, "Continuous tracking", "Live visit check-ins and location so you always know care is happening."], [PhoneOff, "Masked calls", "Contact caregivers via a proxy number — real numbers stay private."], [Siren, "24/7 SOS response", "One tap dispatches our rapid medical response team."]].map(([Ic, t, d], i) => (
              <Reveal key={t} delay={i * 0.08}><div className="bg-white rounded-2xl border border-slate-200 p-6 h-full"><div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-4"><Ic className="w-6 h-6 text-[#D97706]" /></div><h4 className="font-semibold text-[#041E26]">{t}</h4><p className="text-slate-500 text-sm mt-1">{d}</p></div></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="veda-plans" className="py-20 lg:py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-12 text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#D97706]">देखभाल की योजनाएँ · Monthly plans</span>
            <h2 className="font-head text-4xl md:text-5xl font-light text-[#041E26] mt-4 tracking-tight">
              Choose the <span className="italic text-[#7A1E38]">seva</span> that fits your parivaar.
            </h2>
            <p className="mt-3 text-slate-500">Transparent monthly pricing. Cancel anytime. GST included.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <div className={`rounded-3xl border p-8 flex flex-col h-full ${p.highlight ? "border-[#F59E0B] bg-amber-50/50 shadow-xl scale-[1.02]" : "border-slate-200 bg-white"}`}>
                  {p.highlight && <span className="self-start text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full bg-[#F59E0B] text-[#041E26] mb-2">Most popular</span>}
                  <h3 className="font-head text-2xl font-semibold text-[#041E26]">{p.name}</h3>
                  <p className="text-sm text-slate-500">{p.tagline}</p>
                  <div className="mt-4 font-head text-4xl font-semibold text-[#041E26]">₹{p.price}<span className="text-base text-slate-400 font-normal">/mo</span></div>
                  <ul className="mt-5 space-y-2.5 flex-1">{p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />{f}</li>)}</ul>
                  <Button data-testid={`veda-plan-cta-${p.id}`} onClick={goPlans} className={`mt-6 rounded-full font-semibold h-12 ${p.highlight ? "bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26]" : "bg-[#041E26] hover:bg-[#0A2F3C] text-white"}`}>Choose {p.name}</Button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* B2B ENQUIRY */}
      <section id="veda-b2b" className="px-6 lg:px-10 pb-24">
        <div className="max-w-5xl mx-auto rounded-[2rem] bg-[#041E26] p-8 lg:p-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[#F59E0B] text-xs font-bold uppercase tracking-[0.2em] mb-4"><Building2 className="w-4 h-4" /> For Employers & NRIs</div>
            <h2 className="font-head text-3xl md:text-4xl font-light text-white leading-tight">Eldercare as an employee benefit, or bulk plans for your community.</h2>
            <p className="text-white/60 mt-4">Offer Maa Papa Care to your employees' parents, or set up plans for an NRI community. Tell us your needs and our partnerships team will craft a plan.</p>
          </div>
          <div className="bg-white rounded-2xl p-6 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-slate-600">Your name</Label><Input data-testid="enq-name" value={enq.contact_name} onChange={(e) => setEnq({ ...enq, contact_name: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Company</Label><Input data-testid="enq-company" value={enq.company} onChange={(e) => setEnq({ ...enq, company: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Email</Label><Input data-testid="enq-email" value={enq.email} onChange={(e) => setEnq({ ...enq, email: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Phone</Label><Input data-testid="enq-phone" value={enq.phone} onChange={(e) => setEnq({ ...enq, phone: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
            </div>
            <div><Label className="text-xs font-semibold text-slate-600">Approx. members / employees</Label><Input data-testid="enq-employees" value={enq.employees} onChange={(e) => setEnq({ ...enq, employees: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
            <Button data-testid="enq-submit" onClick={submitEnquiry} disabled={sending} className="w-full h-12 rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-bold gap-2">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request a callback"}</Button>
          </div>
        </div>
      </section>

      <footer className="bg-[#041E26] py-10 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <span className="text-white/60 text-sm">
            <span className="font-serif-logo italic text-[#F5D08B]">Maa Papa Care</span> by Candle Healthcare — <span className="italic">Ghar mein diya bhi, doctor bhi.</span>
          </span>
          <button onClick={() => navigate("/")} className="text-[#F59E0B] text-sm font-semibold hover:underline">← Back to Candle Healthcare</button>
        </div>
      </footer>
    </div>
  );
}
